import { useState } from 'react'
import { Buffer }   from 'buffer'
import Consts       from '../Consts'

const SESSION_KEY_USER = 'sk.mediatex.smsplatby.user'
const SESSION_KEY_PHONE = 'sk.mediatex.smsplatby.phone'

const getLocalStorageUser = () => {
  let userJson = localStorage.getItem(SESSION_KEY_USER) ?? null
  return userJson === null ? null : JSON.parse(userJson)
}

let currentUser = getLocalStorageUser()

const setLocalStorageUser = (user) => {
  if (user === null) {
    localStorage.removeItem(SESSION_KEY_USER)
  } else {
    localStorage.setItem(SESSION_KEY_USER, JSON.stringify(user))
  }
}

export const useCurrentUser = () => {

  const [user, setUser] = useState(currentUser)

  const setUserInternal = (usr) => {
    currentUser = usr
    setUser(usr)
    setLocalStorageUser(usr)
  }

  // return global userState state and setter function
  return [user, setUserInternal];
}

const getSessionStoragePhone = () => {
  let phoneAuth = localStorage.getItem(SESSION_KEY_PHONE) ?? null
  return phoneAuth === null ? null : phoneAuth
}

let currentPhone = getSessionStoragePhone()

const setSessionStoragePhone = (phone) => {
  if (phone === null) {
    localStorage.removeItem(SESSION_KEY_PHONE)
  } else {
    localStorage.setItem(SESSION_KEY_PHONE, phone)
  }
}

export const useCurrentPhone = () => {

  const [phone, setPhone] = useState(currentPhone)

  const setPhoneInternal = (tel) => {
    currentPhone = tel
    setPhone(tel)
    setSessionStoragePhone(tel)
  }

  // return global userState state and setter function
  return [phone, setPhoneInternal];
}

export function useApi() {

  const BASE_URL = process.env.REACT_APP_API_URL ?? `https://${window.location.host}/api`

  const [user, setCurrentUser] = useCurrentUser()
  const [phoneAuthToken, setCurrentPhone] = useCurrentPhone()

  const send = (url, options) => {
    return new Promise((resolve, reject) => {
      fetch(url, options)
        .then(async (response) => {
          let data = null
          if (response.headers.get('Content-Type').includes('application/json')) {
            try {
              data = JSON.parse(await response.text())
            } catch (e) {
              data = null
            }
          } else if (response.headers.get('Content-Type').includes('application/pdf') || response.headers.get('Content-Type').includes('text/csv')) {
            data = await response.blob()
          }
          if (response.status / 100 !== 2) {
            if (response.status === 401) {
              setCurrentUser(null)
              setCurrentPhone(null)
              window.location.reload()
            }
            reject({
              ...data,
              status: response.status,
              statusText: response.statusText,
            })
          } else {
            resolve(data)
          }
        })
        .catch(error => {
          console.error(`Api failed on ${options.method.toUpperCase()} ${url}`, error)
          reject(error)
        })
    })
  }

  const encodeURIComponentOwn = (component) => {
    return Array.isArray(component) ? component.map(it => encodeURIComponent(it)).join(',') : encodeURIComponent(component)
  }

  const get = (path, args) => {
    let query = ''
    if (args && Object.getOwnPropertyNames(args).length > 0) {
      query = '?' + (Object.getOwnPropertyNames(args).filter(key => typeof args[key] !== 'undefined' && args[key] !== null).map(key => {
          if (typeof args[key] === 'boolean') {
            return `${key}=${encodeURIComponentOwn(args[key] ? 1 : 0)}`
          }
          return `${key}=${encodeURIComponentOwn(args[key])}`
        }).join('&'))
    }
    let headers = {}
    if (!!user) {
      headers.Authorization = 'Bearer ' + user.accessToken
    } else if (phoneAuthToken !== null) {
      headers.Authorization = 'Basic ' + Buffer.from(phoneAuthToken).toString('base64')
    }
    return send(BASE_URL+path+query, { method: "get", headers: headers, })
  }

  const put = (path, id, data) => {
    let headers = {
      'Content-Type': 'application/json'
    }
    if (user !== null) {
      headers.Authorization = 'Bearer ' + user.accessToken
    }
    return send(BASE_URL+path+`/${id}`, { method: "put", body: JSON.stringify(data), headers: headers, })
  }

  const post = (path, data, headers = {}) => {
    let reqHeaders = {
      'Content-Type': 'application/json',
      ...headers,
    }
    if (user !== null) {
      reqHeaders.Authorization = 'Bearer ' + user.accessToken
    } else if (phoneAuthToken !== null) {
      reqHeaders.Authorization = 'Basic ' + Buffer.from(phoneAuthToken).toString('base64')
    }
    return send(BASE_URL+path, { method: "post", body: JSON.stringify(data), headers: reqHeaders, })
  }

  const _delete = (path) => {
    let headers = { }
    if (!!user) {
      headers.Authorization = 'Bearer ' + user.accessToken
    }
    return send(BASE_URL+path, { method: "delete", headers: headers, })
  }

  const commonApi = {
    user: {
      login: (email, password) => post('/user/login', { email: email, password: password }),
      logout: () => _delete('/user/logout'),
      validateHash: () => get('/user/validateHash', {}),
      detail: (id) => get(`/user/${id}`),
      save: (data) => data.id ? put(`/user`, data.id, data) : post(`/user`, data),
    }
  }

  const adminApi = {
    user: {
      list: (query, types, page, count) => get('/user/list', { query: query, types: types.join(','), page: page, count: count }),
      delete: (id) => _delete(`/user/${id}`)
    },
    message: {
      list: (updatedAfterMillis, dateFrom, dateTo, serviceIds, activeSubscriptions, serviceConnectionType, query, page, count) => get(`/message/list`, { updatedAfterMillis: updatedAfterMillis,
        query: query, dateFrom: dateFrom, dateTo: dateTo,
        serviceIds: serviceIds?.join(','), activeSubscriptions: activeSubscriptions, serviceConnectionType: serviceConnectionType,
        page: page, count: count,
      }),
      detail: (id) => get(`/message/${id}`),
      statistics: (from, to, serviceIds, groupBy) => get(`/message/statistics` + (from && to ? `/${from}-${to}` : '') + (serviceIds ? `/${serviceIds.join(',')}` : ''), {
        groupBy: groupBy?.join(',')
      }),
    },
    transaction: {
      list: (dateFrom, dateTo, serviceIds, query, page, count) => get(`/transaction/list`, {
        query: query, dateFrom: dateFrom, dateTo: dateTo,
        serviceIds: (serviceIds ? serviceIds.join(',') : ''),
        page: page, count: count,
      }),
      detail: (id) => get(`/transaction/${id}`),
      statistics: (from, to, serviceIds, groupBy) => get(`/transaction/statistics` + (from && to ? `/${from}-${to}` : '') + (serviceIds ? `/${serviceIds.join(',')}` : ''), {
        groupBy: groupBy?.join(',')
      }),
    },
    log: {
      messageList: (messageId) => get(`/log/list`, { messageId: messageId }),
      serviceList: (serviceId) => get(`/log/list`, { serviceId: serviceId }),
    },
    operator: {
      list: () => get(`/operator/list`)
    },
    partner: {
      detail: (id) => get(`/partner/${id}`),
      save: (data) => data.id ? put(`/partner`, data.id, data) : post(`/partner`, data),
      delete: (id) => _delete(`/partner/${id}`),
      list: (query, page, count) => get('/partner/list', { query: query, page: page, count: count }),
    },
    service: {
      detail: (id) => get(`/service/${id}`),
      save: (data) => data.id ? put(`/service`, data.id, data) : post(`/service`, data),
      delete: (id) => _delete(`/service/${id}`),
      list: (partnerId, categoryId, query, page, count) => get('/service/list', { partnerId: partnerId, categoryId: categoryId, query: query, page: page, count: count }),
      listByConnectionType: (connectionType, query, page, count) => get('/service/list', { connectionType: connectionType, query: query, page: page, count: count })
    },
    serviceOperator: {
      detail: (id) => get(`/serviceOperator/${id}`),
      save: (data) => data.id ? put(`/serviceOperator`, data.id, data) : post(`/serviceOperator`, data),
      delete: (id) => _delete(`/serviceOperator/${id}`),
      list: (serviceId, query, page, count) => get('/serviceOperator/list', { serviceId: serviceId, query: query, page: page, count: count }),
    },
    share: {
      detail: (id) => get(`/share/${id}`),
      save: (data) => data.id ? put(`/share`, data.id, data) : post(`/share`, data),
      delete: (id) => _delete(`/share/${id}`),
      list: (serviceId, query, page, count) => get('/share/list', { serviceId: serviceId, query: query, page: page, count: count }),
    },
    transactionShare: {
      detail: (id) => get(`/transactionShare/${id}`),
      delete: (id) => _delete(`/transactionShare/${id}`),
      save: (data) => data.id ? put(`/transactionShare`, data.id, data) : post(`/transactionShare`, data),
      list: (serviceId, dateFrom, dateTo, type, query, page, count) => get(`/transactionShare/list`, {
        serviceId: serviceId, query: query, dateFrom: dateFrom, dateTo: dateTo,
        type: type,
        page: page, count: count,
      }),
      listByTransaction: (transactionId) => get(`/transactionShare/list`, {
        transactionId: transactionId,
      }),
      statistics: (from, to, serviceIds, groupBy) => get(`/transactionShare/statistics` + (from && to ? `/${from}-${to}` : '') + (serviceIds ? `/${serviceIds.join(',')}` : ''), {
        groupBy: groupBy?.join(',')
      }),
    },
    digitalAccess: {
      exportCodes: (serviceId) => get(`/digitalAccess/exportCodes/${serviceId}`),
      generate: (serviceId, count, patternWeb, patternServer) => post(`/digitalAccess/generate/${serviceId}`, { patternWeb: patternWeb, patternServer: patternServer, count: count }),
      list: (serviceId, query, page, count) => get(`/digitalAccess/list/${serviceId}`, { query: query, page: page, count: count }),
    },
    customer: {
      list: (page, pageSize) => get(`/customer/transactions`, { page: page, count: pageSize }),
      login: (phone, code) => post(`/customer`, { phone: phone, code: code }),
      download: (id) => get(`/customer/transaction/${id}.pdf`),
    },
    scheduleChange: {
      delete: (changeId) => _delete(`/scheduleChange/${changeId}`),
      service: {
        save: (serviceId, data) => put(`/scheduleChange/service`, serviceId, data),
        list: (serviceId) => get(`/scheduleChange/list/service/${serviceId}`),
      },
      serviceOperator: {
        save: (serviceOperatorId, data) => put(`/scheduleChange/serviceOperator`, serviceOperatorId, data),
        list: (serviceOperatorId) => get(`/scheduleChange/list/serviceOperator/${serviceOperatorId}`),
      },
    },
    servicePeriod: {
      detail: (id) => get(`/servicePeriod/${id}`),
      save: (data) => data.id ? put(`/servicePeriod`, data.id, data) : post(`/servicePeriod`, data),
      delete: (id) => _delete(`/servicePeriod/${id}`),
      list: (serviceId, query, page, count) => get('/servicePeriod/list', { serviceId: serviceId, query: query, page: page, count: count }),
    },
    messageInput: {
      export: (serviceId, dateFrom, dateTo, onlyCorrect) => get(`/messageInput/export`, { serviceId: serviceId, dateFrom: dateFrom, dateTo: dateTo, onlyCorrect: onlyCorrect })
    },
    infoAnswer: {
      detail: (id) => get(`/infoAnswer/${id}`),
      save: (data) => put(`/infoAnswer`, data.id, data),
      bulkSave: (data) => post(`/infoAnswer/save`, data),
      delete: (id) => _delete(`/infoAnswer/${id}`),
      list: (serviceId, query, page, count) => get('/infoAnswer/list', { serviceId: serviceId, query: query, page: page, count: count }),
    },
    serviceEmail: {
      detail: (id) => get(`/serviceEmail/${id}`),
      save: (data) => data.id ? put(`/serviceEmail`, data.id, data) : post(`/serviceEmail`, data),
      delete: (id) => _delete(`/serviceEmail/${id}`),
      list: (serviceId, query, page, count) => get('/serviceEmail/list', { serviceId: serviceId, query: query, page: page, count: count }),
    }
  }

  const partnerApi = {
    message: {
      list: (updatedAfterMillis, dateFrom, dateTo, query, page, count) => get(`/user-partner/message/list`, {updatedAfterMillis: updatedAfterMillis,
        query: query, dateFrom: dateFrom, dateTo: dateTo,
        page: page, count: count,
      }),
      detail: (id) => get(`/user-partner/message/${id}`),
      statistics: (from, to, serviceIds, groupBy) => get(`/user-partner/message/statistics` + (from && to ? `/${from}-${to}` : '') + (serviceIds ? `/${serviceIds.join(',')}` : ''), {
        groupBy: groupBy?.join(',')
      }),
    },
    transaction: {
      list: (dateFrom, dateTo, serviceIds, query, page, count) => get(`/user-partner/transaction/list`, {
        query: query, dateFrom: dateFrom, dateTo: dateTo,
        serviceIds: (serviceIds ? serviceIds.join(',') : ''),
        page: page, count: count,
      }),
      detail: (id) => get(`/user-partner/transaction/${id}`),
      statistics: (from, to, serviceIds, groupBy) => get(`/user-partner/transaction/statistics` + (from && to ? `/${from}-${to}` : '') + (serviceIds ? `/${serviceIds.join(',')}` : ''), {
        groupBy: groupBy?.join(',')
      }),
    },
    transactionShare: {
      statistics: (from, to, serviceIds, groupBy) => get(`/user-partner/transactionShare/statistics` + (from && to ? `/${from}-${to}` : '') + (serviceIds ? `/${serviceIds.join(',')}` : ''), {
        groupBy: groupBy?.join(',')
      }),
    },
    service: {
      list: (partnerId, query, page, count) => get('/user-partner/service/list', { partnerId: partnerId, query: query, page: page, count: count }),
    },
    log: {
      messageList: (messageId) => get(`/user-partner/log/list`, { messageId: messageId }),
    },
  }

  if (user?.type === Consts.User.typePartner) {
    return {
      ...commonApi,
      ...partnerApi
    }
  }
  return {
    ...commonApi,
    ...adminApi,
    user: {
      ...commonApi.user,
      ...adminApi.user
    },
    category: {
      detail: (id) => get(`/category/${id}`),
      save: (data) => data.id ? put(`/category`, data.id, data) : post(`/category`, data),
      delete: (id) => _delete(`/category/${id}`),
      list: (query, page, count) => get('/category/list', { query: query, page: page, count: count }),
    }
  }
}